import { useQuery } from '@apollo/client'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Backdrop, CircularProgress, Theme, Typography, makeStyles } from '@material-ui/core'
import {
    GET_SPEC_INTELLIGENCE_REPORT,
    GET_SPEC_INTELLIGENCE_REPORT_HISTORY,
} from 'api/graphQL/queries/specIntelligence.queries'
import type {
    GetSpecIntelligenceReportHistoryInput,
    GetSpecIntelligenceReportHistoryResponse,
    GetSpecIntelligenceReportInput,
    GetSpecIntelligenceReportResponse,
} from 'api/graphQL/types/specIntelligence.types'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import InsightsRunDetail from './Insights/InsightsRunDetail'
import InsightsRunsList from './Insights/InsightsRunsList'

/**
 * Vertical space taken by the app bar, action bar, and sub-tab nav above the Insights
 * content — subtracted from the viewport height so the run list/detail scroll within
 * the page rather than pushing it. Mirrors ReportsPanel's CONTENT_HEIGHT_OFFSET_PX.
 */
const CONTENT_HEIGHT_OFFSET_PX = 144

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'row',
        height: `calc(100vh - ${CONTENT_HEIGHT_OFFSET_PX}px)`,
        overflow: 'hidden',
    },
    emptyState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: theme.spacing(20),
        width: '100%',
    },
    detailLoading: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F7F8FA',
    },
    emptyStateMessage: {
        color: '#384F5E',
        fontSize: pxToRem(18),
        fontWeight: 400,
    },
}))

const NO_RUNS_MESSAGE = 'No Insights runs yet for this project.'
const REPORT_LOAD_ERROR_MESSAGE = 'Unable to load this run. Please try again.'

/**
 * Read-only browser for past SpecIntelligence ("Insights") runs on a project.
 * Shows run history newest-first on the left (auto-selecting the newest run),
 * and the selected run's findings — filterable by Category and State — on the right.
 */
const InsightsPanel = () => {
    const classes = useStyles()
    const { projectId } = useParams<{ projectId?: string }>()
    const [userSelectedId, setUserSelectedId] = useState<string | null>(null)

    const { data: historyData, loading: historyLoading } = useQuery<
        GetSpecIntelligenceReportHistoryResponse,
        GetSpecIntelligenceReportHistoryInput
    >(GET_SPEC_INTELLIGENCE_REPORT_HISTORY, {
        variables: { input: { projectId: projectId as string } },
        skip: !projectId,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
    })

    const runs = useMemo(
        () =>
            [...(historyData?.getSpecIntelligenceReportHistory ?? [])].sort(
                (a, b) => new Date(b.runDate).getTime() - new Date(a.runDate).getTime()
            ),
        [historyData]
    )

    const selectedReportId = userSelectedId ?? runs[0]?.id ?? null

    const {
        data: reportData,
        loading: reportLoading,
        error: reportError,
    } = useQuery<GetSpecIntelligenceReportResponse, GetSpecIntelligenceReportInput>(GET_SPEC_INTELLIGENCE_REPORT, {
        variables: { input: { reportId: selectedReportId as string, includeResolved: true } },
        skip: !selectedReportId,
    })

    const selectedReport = reportData?.getSpecIntelligenceReport ?? null

    const renderDetailPane = () => {
        if (reportError) {
            return (
                <div className={classes.detailLoading}>
                    <Typography component="span" className={classes.emptyStateMessage}>
                        {REPORT_LOAD_ERROR_MESSAGE}
                    </Typography>
                </div>
            )
        }
        if (reportLoading || !selectedReport) {
            return (
                <div className={classes.detailLoading}>
                    <CircularProgress />
                </div>
            )
        }
        return <InsightsRunDetail report={selectedReport} projectId={projectId as string} />
    }

    if (historyLoading && !historyData) {
        return (
            <Backdrop open invisible>
                <CircularProgress />
            </Backdrop>
        )
    }

    if (runs.length === 0) {
        return (
            <div className={classes.emptyState}>
                <Typography component="span" className={classes.emptyStateMessage}>
                    {NO_RUNS_MESSAGE}
                </Typography>
            </div>
        )
    }

    return (
        <div className={classes.container} data-testid="insightsPanel">
            <InsightsRunsList runs={runs} selectedId={selectedReportId} onSelect={setUserSelectedId} />
            {renderDetailPane()}
        </div>
    )
}

export default InsightsPanel
